import React from 'react';
import { makeStyles } from '@mui/styles';
import { Typography } from '@mui/material';
import { Box } from '@mui/system';
import clsx from 'clsx';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import colors from '../../utils/colors';

const useStyles = makeStyles(() => ({
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
  },
  pill: {
    display: 'inline-flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '10px 8px',
    paddingLeft: '12px',
    paddingRight: '12px',
    //minHeight: '10px',
    borderRadius: '32px',
    border: `1.5px solid ${colors.BLUE}`,
    backgroundColor: '#EDF3F8',
    fontFamily: 'Inter',
    fontSize: '15px',
    fontWeight: 600,
    lineHeight: 1.3,
    color: colors.TITLE_GRAY,
    cursor: 'pointer',
    outline: 'none',
    userSelect: 'none',
    boxSizing: 'border-box',
    whiteSpace: 'nowrap',
    transition: 'background-color 150ms ease, border-color 150ms ease',
    '&:hover': { backgroundColor: '#DCE9F5' },
  },
  pillSelected: {
    backgroundColor: colors.BLUE,
    color: colors.WHITE,
    '&:hover': { backgroundColor: colors.BLUE, },
  },
  pillDisabled: {
    cursor: 'not-allowed',
    border: `1.5px solid ${colors.SUBTLE_GRAY}`,
    backgroundColor: colors.WHITE,
    color: colors.GRAY_SECONDARY,
    '&:hover': { backgroundColor: colors.WHITE },
  },
  // "block" variant -- large, evenly-split segmented buttons (Classification section's
  // Urgency/Clinical flag toggles) instead of content-sized chips (determination type,
  // denial reason code), so each option reads as a big tappable choice rather than a tag.
  rowBlock: {
    flexWrap: 'nowrap',
  },
  pillBlock: {
    flex: '1 1 0',
    minHeight: '20px',
    padding: '12px 16px',
    fontSize: '15px',
    fontWeight: 600,
    whiteSpace: 'normal',
    textAlign: 'center',
  },
  checkIcon: {
    fontSize: '18px',
    color: colors.WHITE,
    flexShrink: 0,
  },
  message: {
    fontFamily: 'Inter',
    fontSize: '14px',
    color: colors.GRAY_SECONDARY,
  },
}));

// Pill/chip single-select, matching the Enterprise Partner Portal's ChipOptionList
// (CustomerSupport/CallSupport/SelectionControls.js) -- used in place of a native/custom
// dropdown for request type, determination type, and denial reason code, so every option
// and the current pick stay visible at once instead of being hidden behind a click-to-open
// menu. option shape: { value, label } or { value, primary } (both used across callers).
const PillOptionList = ({
  id,
  ariaLabel,
  options = [],
  value,
  onChange,
  disabled = false,
  loading = false,
  loadingText = 'Loading…',
  emptyText = 'No options available',
  variant = 'chip',
}) => {
  const classes = useStyles();
  const isBlock = variant === 'block';

  if (loading) {
    return <Typography className={classes.message}>{loadingText}</Typography>;
  }

  if (options.length === 0) {
    return <Typography className={classes.message}>{emptyText}</Typography>;
  }

  return (
    <Box id={id} className={clsx(classes.row, isBlock && classes.rowBlock)} role="radiogroup" aria-label={ariaLabel}>
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <Box
            key={option.value}
            role="radio"
            aria-checked={selected}
            tabIndex={disabled ? -1 : 0}
            className={clsx(
              classes.pill,
              isBlock && classes.pillBlock,
              selected && classes.pillSelected,
              disabled && classes.pillDisabled,
            )}
            onClick={() => !disabled && onChange(option.value)}
            onKeyDown={(e) => {
              if (disabled) {
                return;
              }
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onChange(option.value);
              }
            }}
          >
            {selected && <CheckCircleIcon className={clsx(classes.checkIcon, isBlock && classes.checkIconBlock)} />}
            {option.label || option.primary}
          </Box>
        );
      })}
    </Box>
  );
};

export default PillOptionList;
